import PromiseQueue from 'p-queue';

import Tasks from 'APP/Tasks';
import { getCommitMessages } from 'APP/Tasks/e2ee/getCommitMessages';
import { ModelError } from 'APP/model/error/errors/ModelError';
import { MessageModelErrorCode } from 'APP/model/message/messageModel.types';
import { messageModel } from 'APP/model/model';
import { broadcastSentMessage } from 'APP/packages/bus/adapters/messaging/sentMessage';
import { MlsStorageService } from 'SERVICES/mlsStorageService/MlsStorageService';
import Entities from 'STORE';
import type { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

const queue: Record<string, PromiseQueue> = {};

interface ISendNewMessageInQueueData {
  message: ChatMessage;
  errorCallback: (error: ModelError) => void;
  retry?: boolean;
}

async function sendRequest(
  data: ISendNewMessageInQueueData,
  resendAfterSync?: boolean
): Promise<void> {
  const { message, errorCallback, retry = false } = data;
  try {
    message.setIsProgress(true);

    const response = await messageModel.sendNewMessage({
      ...message.toJSON(),
      retry,
    });

    if (!message.isEditQueue) {
      const group = Entities.GroupsStore.getGroupById(message.groupId);

      // ToDo: updateExpectedServerTime - при разрыве соединения нужно начитывать утерянные сообщения и апать
      //  ExpectedServerTime, чтобы данное сообщение оказалось в актуальном месте
      // желательно отрефакторить логику и вынести начитку за пределы отправки.
      Tasks.messaging.updateOldOrFakeMessages({
        messages: [response.message],
        groupId: message.groupId,
        prevMessageTs: Number(response.previousMessageTs),
        updateExpectedServerTime: !!group && group.lastMessageTs < response.previousMessageTs,
      });

      if (!!group && group.lastMessageTs < response.previousMessageTs) {
        await Tasks.messaging.handleNewMessages(
          [response.message],
          Number(response.previousMessageTs)
        );
      }
    } else {
      message.setId(response.message.id);
    }

    message.setIsProgress(false);
    broadcastSentMessage(message.groupId, {
      messageUuid: response.message.clientUuid,
      messageId: response.message.id,
      isRequestFinished: true,
      hasError: false,
    });
  } catch (error) {
    if (
      error.message === MessageModelErrorCode.SecretMessageCommitSyncRequired &&
      !resendAfterSync
    ) {
      const mlsGroup = MlsStorageService.loadGroup(data.message.groupId);

      if (!mlsGroup) {
        throw new Error('MlsGroup does not exist');
      }

      await getCommitMessages(data.message.groupId, Number(mlsGroup.currentEpoch()));

      const newMlsGroup = MlsStorageService.loadGroup(data.message.groupId);
      if (newMlsGroup) {
        data.message.payload.setEpoch(Number(newMlsGroup.currentEpoch()));
      }

      await sendRequest(data, true);
      return;
    }
    message.setIsProgress(false);

    broadcastSentMessage(message.groupId, {
      messageUuid: message.clientUuid,
      messageId: message.id,
      isRequestFinished: true,
      hasError: true,
    });

    if (error.message === MessageModelErrorCode.QuotedMessageIsNotExist && message.quote) {
      handleQuotedMessageNotExistError(message);
      return;
    }

    errorCallback(error);
  }
}

export const sendNewMessageInQueue = async (data: ISendNewMessageInQueueData): Promise<void> => {
  if (!queue[data.message.groupId]) {
    queue[data.message.groupId] = new PromiseQueue({ concurrency: 1 });
  }

  queue[data.message.groupId].add(() => sendRequest(data));
};

function handleQuotedMessageNotExistError(message: ChatMessage): void {
  const userText = message.renderPayload.text || message.renderPayload.comment || '';

  Tasks.messaging.handleInvalidQuote({
    groupId: message.groupId,
    userText,
  });

  const group = Entities.GroupsStore.getGroupById(message.groupId);
  if (group) {
    group.messagesStore.removeMessageById(message.id);
  }
}
